import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";
import { useCallback, useEffect } from "react";

import type { CrmDataProvider } from "../../providers/types";
import type { RAFile } from "../../types";
import type {
  EmailLinks,
  MailboxFile,
  MailboxMessageRef,
  MailboxPage,
} from "../mailTypes";
import { mailErrorKey } from "../useMailAccount";

/** Mailbox data is private: never saved for offline use. */
const PRIVATE = { persist: false };

export const MAILBOX_QUERY_KEY = ["mailbox"];

export const useMailboxFolders = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: ["mailbox", "folders"],
    queryFn: () => dataProvider.getMailboxFolders(),
    meta: PRIVATE,
  });
};

export const useMailboxMessages = (params: {
  folder: string;
  page: number;
  q: string;
}) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: ["mailbox", "messages", params.folder, params.page, params.q],
    queryFn: () => dataProvider.getMailboxMessages(params),
    placeholderData: keepPreviousData,
    meta: PRIVATE,
  });
};

/** One message; opening it marks it read in the list and the counters. */
export const useMailboxMessage = (ref: MailboxMessageRef | null) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["mailbox", "message", ref?.folder, ref?.uid],
    queryFn: () => dataProvider.getMailboxMessage(ref as MailboxMessageRef),
    enabled: ref != null,
    meta: PRIVATE,
  });

  const openedUid = query.data?.uid;
  const openedFolder = query.data?.folder;
  useEffect(() => {
    if (openedUid == null || openedFolder == null) return;
    queryClient.setQueriesData<MailboxPage>(
      { queryKey: ["mailbox", "messages", openedFolder] },
      (page) =>
        page && {
          ...page,
          messages: page.messages.map((message) =>
            message.uid === openedUid ? { ...message, seen: true } : message,
          ),
        },
    );
    queryClient.invalidateQueries({ queryKey: ["mailbox", "folders"] });
  }, [openedUid, openedFolder, queryClient]);

  return query;
};

export const useDeleteMailboxMessage = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation({
    mutationFn: (ref: MailboxMessageRef) =>
      dataProvider.deleteMailboxMessage(ref),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MAILBOX_QUERY_KEY });
      notify("crm.mailbox.deleted", { type: "success" });
    },
    onError: (error) => notify(mailErrorKey(error), { type: "error" }),
  });
};

export const useAddMailboxMessageToCrm = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  return useMutation({
    mutationFn: (input: MailboxMessageRef & EmailLinks) =>
      dataProvider.addMailboxMessageToCrm(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailbox", "message"] });
      queryClient.invalidateQueries({ queryKey: ["emails"] });
      queryClient.invalidateQueries({ queryKey: ["activity_log"] });
      notify("crm.mailbox.added_to_feed", { type: "success" });
    },
    onError: (error) => notify(mailErrorKey(error), { type: "error" }),
  });
};

/** A downloaded file as a browser File. */
export const toBrowserFile = (file: MailboxFile): File => {
  const bytes = Uint8Array.from(atob(file.data), (char) => char.charCodeAt(0));
  return new File([bytes], file.filename, { type: file.content_type });
};

/** Files of a message: saved on the computer, or joined to a forward. */
export const useMailboxAttachments = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();

  const download = useCallback(
    async (ref: MailboxMessageRef, index: number) => {
      try {
        const file = toBrowserFile(
          await dataProvider.getMailboxAttachment({ ...ref, index }),
        );
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        notify(mailErrorKey(error), { type: "error" });
      }
    },
    [dataProvider, notify],
  );

  /** Every file that could be fetched; the others are reported. */
  const asAttachments = useCallback(
    async (ref: MailboxMessageRef, indexes: number[]): Promise<RAFile[]> => {
      const files: RAFile[] = [];
      for (const index of indexes) {
        try {
          const file = toBrowserFile(
            await dataProvider.getMailboxAttachment({ ...ref, index }),
          );
          files.push({
            rawFile: file,
            src: URL.createObjectURL(file),
            title: file.name,
            type: file.type,
          });
        } catch (error) {
          notify(mailErrorKey(error), { type: "warning" });
        }
      }
      return files;
    },
    [dataProvider, notify],
  );

  return { download, asAttachments };
};
